import { Box, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

import { LicenseAssignmentInfo } from '@localstack/types';
import { ReactElement, useState } from 'react';

import { OrganizationsService, SubscriptionService, useApiEffect, useApiGetter } from '@localstack/services';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { SELF_ASSIGN_NOT_PERMITTED_MESSAGE, SELF_ASSIGN_NO_OPTIONS_MESSAGE } from '~/util/license';

import { LicenseAssignmentPlanLabel } from '~/components/MembersAndLicenses/LicenseAssignmentPlanLabel';

import { STATUS_ICONS } from './icons';
import { SelfAssignDropdown } from './SelfAssignDropdown';

export const AssignmentBadgeBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  paddingRight: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  border: '1px solid',
  borderColor: theme.palette.action.disabled,
  borderRadius: theme.spacing(0.5),
}));

const AssignmentBadges = ({ assignments }: { assignments: LicenseAssignmentInfo[] }): ReactElement => (
  <Box mt={1} display="flex" flexDirection="row" flexWrap="wrap" gap={1}>
    {assignments.map((assignment) => (
      <AssignmentBadgeBox key={assignment.subscription_id}>
        <LicenseAssignmentPlanLabel emulator={assignment.emulator} family={assignment.plan_family} />
      </AssignmentBadgeBox>
    ))}
  </Box>
);

export const LicenseAssigned = (): ReactElement => {
  const { userInfo } = useAuthProvider();

  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');

  const { data: assignments, isLoading: isAssignmentInfoLoading } = useApiGetter(
    OrganizationsService,
    'listLicenseAssignmentsForUser',
    ['my'],
    { suppressErrors: true },
  );

  const hasLicenseAssigned = !!assignments?.length;

  const { data: preflight, isLoading: isPreflightLoading } = useApiGetter(
    OrganizationsService,
    'preflightSelfAssignLicense',
    ['my'],
    {
      // Fetched even once a license is assigned: a user can self-assign one license per emulator
      // type, so someone with e.g. Snowflake assigned may still have AWS available to self-assign.
      enable: !!userInfo && !!assignments,
      suppressErrors: true,
    },
  );

  const { selfAssignLicense, isLoading: isIssuingLicense } = useApiEffect(OrganizationsService, ['selfAssignLicense'], {
    revalidate: ['listLicenseAssignmentsForUser', 'listLicenseAssignments', 'preflightSelfAssignLicense'],
    revalidateOtherClient: { client: [SubscriptionService], methods: ['getAggregatedSubscriptionInfo'] },
  });

  const loadingLicenseSection = isAssignmentInfoLoading || isPreflightLoading;

  const assignableOptions = preflight?.options ?? [];

  if (loadingLicenseSection) {
    return (
      <Box display="flex" flexDirection="row" gap={1}>
        <Skeleton variant="circular" width={18} height={18} />
        <Box sx={{ width: '100%' }}>
          <Skeleton width="50%" variant="text" />
        </Box>
      </Box>
    );
  }

  const selfAssignDropdown = preflight?.permitted === true && assignableOptions.length > 0 && (
    <SelfAssignDropdown
      options={assignableOptions}
      selectedSubscriptionId={selectedSubscriptionId}
      onChangeSelection={setSelectedSubscriptionId}
      onSubmit={() => selfAssignLicense('my', { subscription_id: selectedSubscriptionId })}
      isSubmitting={isIssuingLicense}
    />
  );

  if (!hasLicenseAssigned) {
    return (
      <Box display="flex" flexDirection="row" gap={1}>
        {STATUS_ICONS.warning}
        <Box sx={{ width: '100%' }}>
          <Typography fontWeight="bold">Emulator Access</Typography>
          <Typography>A license is required to activate LocalStack.</Typography>
          <Box mt={1}>
            {preflight?.permitted === false && <Typography>{SELF_ASSIGN_NOT_PERMITTED_MESSAGE}</Typography>}
            {preflight?.permitted === true && assignableOptions.length === 0 && (
              <Typography>{SELF_ASSIGN_NO_OPTIONS_MESSAGE}</Typography>
            )}
            {selfAssignDropdown}
          </Box>
        </Box>
      </Box>
    );
  }

  const activeAssignments = assignments.filter((assignment) => assignment.is_sub_active);
  const inactiveAssignments = assignments.filter((assignment) => !assignment.is_sub_active);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {activeAssignments.length > 0 && (
        <Box display="flex" flexDirection="row" gap={1}>
          {STATUS_ICONS.success}
          <Box sx={{ width: '100%' }}>
            <Typography fontWeight="bold">Emulator Access</Typography>
            <Typography>Your assigned licenses let this token activate:</Typography>
            <AssignmentBadges assignments={activeAssignments} />
          </Box>
        </Box>
      )}
      {inactiveAssignments.length > 0 && (
        <Box display="flex" flexDirection="row" gap={1}>
          {STATUS_ICONS.warning}
          <Box sx={{ width: '100%' }}>
            <Typography fontWeight="bold">License Expired</Typography>
            <Typography>
              {inactiveAssignments.length > 1
                ? 'The subscriptions behind the following license assignments are no longer active.'
                : 'The subscription behind the following license assignment is no longer active.'}{' '}
              Reach out to your workspace admin to restore access:
            </Typography>
            <AssignmentBadges assignments={inactiveAssignments} />
          </Box>
        </Box>
      )}
      {selfAssignDropdown && (
        <Box display="flex" flexDirection="row" gap={1}>
          {STATUS_ICONS.add}
          <Box sx={{ width: '100%' }}>
            <Typography fontWeight="bold">Self Assign Licenses</Typography>
            <Typography>You can self-assign a license for another product:</Typography>
            <Box mt={1}>{selfAssignDropdown}</Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
